import Popper from '@mui/material/Popper';
import { alpha } from '@mui/system';
import React, {
  FocusEventHandler,
  FormEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  Reducer,
  useCallback,
  useDeferredValue,
  useReducer,
  useRef,
} from 'react';
import { Input, InputProps, InputTokens } from '@youhelp-ru/ui';
import { styled } from '@mui/material/styles';
import { useTheme } from '../../../../../hooks/useTheme';
import { Paper } from '../../../../paper';
import { MenuItem, MenuList } from '../../../../menuList';
import { SxProps } from '../../../../../theme';
import { AUTOCOMPLETE_POPPER_BOX_SHADOW_OPACITY, AUTOCOMPLETE_QUERY_MIN_LENGTH_TO_REQUEST_SUGGESTIONS_DEFAULT } from './const';

export interface AutocompleteOption {
  label: string;
  value?: string | number;
  disabled?: boolean;
}

export type OptionsProvider<T extends AutocompleteOption> = (query: string, opts?: { signal?: AbortSignal }) => Promise<T[]>;

export interface AutocompleteInputProps<T extends AutocompleteOption = AutocompleteOption> extends Omit<InputProps, 'sx'> {
  sx?: SxProps;
  zIndex?: number;
  getOptions: OptionsProvider<T>;
  renderOption?: (option: T) => React.ReactNode | null;
  queryMinLengthToRequestSuggestions?: number;
}

const setNativeValue = (element: Element, value: string) => {
  const valueSetter = Object.getOwnPropertyDescriptor(element, 'value')?.set;
  const prototype = Object.getPrototypeOf(element);
  const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;

  if (valueSetter && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter?.call(element, value);
  } else {
    valueSetter?.call(element, value);
  }
};

const Root = styled('span', { name: 'Autocomplete' })(({ theme }) => ({
  [InputTokens.ColorBorder]: theme.palette['gray light'],
  [InputTokens.ColorBorderFocus]: theme.palette.gray,
  [InputTokens.ColorBackground]: theme.palette.background.default,
  [InputTokens.DimensionRadius]: `${theme.borderRadius.normal}px`,
  display: 'inline-flex',
  alignSelf: 'stretch',
}));

const AUTOCOMPLETE_FIELD_SUGGESTIONS_LIST_MAX_HEIGHT = 200;

type AutocompleteState = { options: AutocompleteOption[]; popoverFocused: boolean; inputFocused: boolean; open: boolean; selected: string };
type AutocompleteAction =
  | { type: 'focus'; input: boolean; popover: boolean }
  | { type: 'options'; options: AutocompleteOption[] }
  | { type: 'autocomplete'; label: string };
const reducer: Reducer<AutocompleteState, AutocompleteAction> = (state, action) => {
  switch (action.type) {
    case 'autocomplete':
      return { ...state, selected: action.label, open: false };
    case 'options':
      return { ...state, options: action.options || [], open: Boolean(state.options.length) && state.inputFocused };
    case 'focus':
      return {
        ...state,
        open: Boolean(state.options.length) && (action.input || action.popover),
        inputFocused: action.input,
        popoverFocused: action.popover,
      };
    default:
      return state;
  }
};

export const AutocompleteInput: <T extends AutocompleteOption>(p: AutocompleteInputProps<T>) => React.ReactNode = ({
  renderOption = (option) => option.label,
  getOptions,
  queryMinLengthToRequestSuggestions = AUTOCOMPLETE_QUERY_MIN_LENGTH_TO_REQUEST_SUGGESTIONS_DEFAULT,
  sx,
  zIndex: baseZIndex = 1,
  // @ts-expect-error do not use incoming ref
  ref: _ref,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const poperRef = React.useRef<HTMLDivElement>(null);
  const anchorEl = rootRef.current;
  const theme = useTheme();
  const abortControllerRef = useRef<AbortController>(new AbortController());

  const [state, dispatch] = useReducer(reducer, { options: [], open: false, popoverFocused: false, inputFocused: false, selected: '' });
  const open = useDeferredValue(state.open);
  const zIndex = baseZIndex + (open ? 2 : 0);

  const onFocusChange: FocusEventHandler = useCallback(
    (e) =>
      dispatch({
        type: 'focus',
        input: e.currentTarget.contains(document.activeElement),
        popover: Boolean(poperRef.current?.contains(document.activeElement)),
      }),
    [],
  );
  const setInputValue = useCallback((nextValue: string) => {
    const input = inputRef.current;
    if (!input) {
      console.warn('Could not find input to set value', nextValue);
      return;
    }
    input.focus();
    setNativeValue(input, nextValue);
    input.dispatchEvent(new InputEvent('input', { data: nextValue, inputType: 'autoComplete', bubbles: true, composed: true }));
    input.dispatchEvent(new CustomEvent('autocomplete', { detail: nextValue, bubbles: true, composed: true }));
    dispatch({ type: 'autocomplete', label: nextValue });
  }, []);

  const onListClick: MouseEventHandler<HTMLLIElement> = useCallback(
    (e) => {
      const itemValue = e.currentTarget.getAttribute('value');
      if (itemValue) {
        setInputValue(itemValue);
      }
    },
    [setInputValue],
  );
  const onListKeyDown: KeyboardEventHandler<HTMLUListElement> = useCallback(
    (e) => {
      if (e.key !== 'Enter') {
        return;
      }
      const itemValue = (e.target as Element).getAttribute('value');
      if (itemValue) {
        setInputValue(itemValue);
      }
    },
    [setInputValue],
  );

  const stopPropagation = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
  }, []);

  const onInputChange: FormEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (!e.isTrusted) {
        dispatch({ type: 'autocomplete', label: e.currentTarget.value });
        // skip autocomplete events
        return;
      }
      const value = e.currentTarget.value;
      if (value.length >= queryMinLengthToRequestSuggestions) {
        abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();
        getOptions(value, { signal: abortControllerRef.current.signal }).then((options) => dispatch({ type: 'options', options }));
      }
    },
    [getOptions, queryMinLengthToRequestSuggestions],
  );

  return (
    <Root onBlur={onFocusChange} onFocus={onFocusChange} ref={rootRef} sx={sx}>
      <Input
        aria-haspopup="listbox"
        autoComplete="off"
        controlRef={inputRef}
        onChangeCapture={onInputChange}
        spellCheck="false"
        sx={{ zIndex }}
        type="text"
        {...props}
      />
      <Popper
        anchorEl={anchorEl}
        disablePortal
        keepMounted
        open={open}
        role="presentation"
        style={{
          width: anchorEl ? anchorEl.clientWidth : undefined,
          zIndex: zIndex - 1,
        }}
      >
        <Paper
          onTouchEnd={stopPropagation}
          onTouchMove={stopPropagation}
          onTouchStart={stopPropagation}
          ref={poperRef}
          sx={{
            width: anchorEl?.getBoundingClientRect().width,
            backgroundColor: theme.palette.background.default,
            boxShadow: `0px 4px 16px ${alpha(theme.palette.black as string, AUTOCOMPLETE_POPPER_BOX_SHADOW_OPACITY)}`,
            borderRadius: `${theme.borderRadius.normal}px`,
            overflow: 'hidden',
            '[data-popper-placement="bottom"] > &': {
              marginTop: `-${theme.borderRadius.normal}px`,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              paddingTop: `${theme.borderRadius.normal}px`,
            },
            '[data-popper-placement="top"] > &': {
              marginBottom: `-${theme.borderRadius.normal}px`,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              paddingBottom: `${theme.borderRadius.normal}px`,
            },
            '&:focus, &:active': {
              outline: 'none',
            },
          }}
        >
          <MenuList
            onKeyDown={onListKeyDown}
            role="listbox"
            sx={{
              overflowX: 'hidden',
              overflowY: 'auto',
              maxHeight: AUTOCOMPLETE_FIELD_SUGGESTIONS_LIST_MAX_HEIGHT,
            }}
          >
            {state.options.map((item, i) => (
              <MenuItem
                disabled={item.disabled}
                key={item.label + i}
                onClick={onListClick}
                tabIndex={item.disabled ? -1 : 0}
                value={typeof item.value !== 'undefined' ? item.value : item.label}
              >
                {renderOption(item as any)}
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popper>
    </Root>
  );
};
